import { FC, useState } from "react"
import { Box, Button, Menu, MenuItem, Stack } from "@mui/material"
import { Add } from "@mui/icons-material"
import { v4 as uuidv4 } from "uuid"
import { NodeFormState } from "./chainSerializer"
import { NodeEditor } from "./nodeEditor"
import { UserbotCatalog } from "../../interfaces"

interface IProps {
  nodes: NodeFormState[]
  descriptors: UserbotCatalog.ActionDescriptor[]
  nodeKinds: UserbotCatalog.NodeDescriptor[]
  limits?: UserbotCatalog.CatalogLimits
  advanced: boolean
  extraHints?: string[]
  /** Сборщик {{stepId.result.*}}-плейсхолдеров для автокомплита полей узла. */
  getResultHints?: (currentNodeId: string) => string[]
  pathPrefix?: string
  onChange: (nodes: NodeFormState[]) => void
}

export const emptyNodeForKind = (kind: string): NodeFormState => {
  const base = {
    key: uuidv4(),
    id: "",
    saves: [],
  }
  switch (kind) {
    case "action":
      return {
        ...base,
        kind: "action",
        action: "",
        paramsValues: {},
        rawParamsJson: "",
      } as NodeFormState
    case "branch":
      return {
        ...base,
        kind: "branch",
        cases: [
          {
            key: uuidv4(),
            when: { op: "eq", left: true, right: true },
            then: [],
          },
        ],
      } as NodeFormState
    case "forEach":
      return {
        ...base,
        kind: "forEach",
        items: [],
        itemVar: "item",
        body: [],
      } as NodeFormState
    case "callChain":
      return {
        ...base,
        kind: "callChain",
        templateId: "",
        clientId: "",
        name: "",
      } as NodeFormState
    case "wait":
      return {
        ...base,
        kind: "wait",
        durationMs: "",
      } as NodeFormState
    case "flow.onUpdate":
      return {
        ...base,
        kind: "flow.onUpdate",
        match: null,
        maxFires: "",
        steps: [],
      } as NodeFormState
    default:
      return {
        ...base,
        kind: "action",
        action: "",
        paramsValues: {},
      } as NodeFormState
  }
}

export const Slot: FC<IProps> = ({
  nodes,
  descriptors,
  nodeKinds,
  limits,
  advanced,
  extraHints,
  getResultHints,
  pathPrefix = "",
  onChange,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  const updateNode = (index: number, node: NodeFormState) =>
    onChange(nodes.map((n, i) => (i === index ? node : n)))

  const removeNode = (index: number) =>
    onChange(nodes.filter((_, i) => i !== index))

  const moveNode = (index: number, dir: -1 | 1) => {
    const target = index + dir
    if (target < 0 || target >= nodes.length) return
    const next = [...nodes]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  const addNode = (kind: string) => {
    onChange([...nodes, emptyNodeForKind(kind)])
    setMenuAnchor(null)
  }

  return (
    <Box>
      <Stack spacing={1}>
        {nodes.map((node, index) => (
          <NodeEditor
            key={node.key}
            node={node}
            index={index}
            total={nodes.length}
            descriptors={descriptors}
            nodeKinds={nodeKinds}
            limits={limits}
            advanced={advanced}
            extraHints={extraHints}
            getResultHints={getResultHints}
            pathPrefix={pathPrefix}
            onChange={(n) => updateNode(index, n)}
            onRemove={() => removeNode(index)}
            onMove={(dir) => moveNode(index, dir)}
          />
        ))}
      </Stack>
      <Box mt={1}>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          Добавить узел
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
        >
          {nodeKinds.map((k) => (
            <MenuItem key={k.kind} onClick={() => addNode(k.kind)}>
              {k.title} ({k.kind})
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  )
}
